import React, { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { Heart, ShoppingCart, Star, Minus, Plus, Truck, ShieldCheck, MessageCircle, ChevronRight } from "lucide-react";
import api, { formatRp, imageUrl } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { useCart } from "@/context/CartContext";
import { useAuth } from "@/context/AuthContext";
import { fetchProductSEO } from "@/context/SEOContext";
import { useBrandName } from "@/context/BrandingContext";
import SEOHead from "@/components/SEOHead";
import { productJsonLd, breadcrumbJsonLd } from "@/lib/structuredData";
import ProductCard from "@/components/ProductCard";
import { toast } from "sonner";

export default function ProductDetailPage() {
  const { slug } = useParams();
  const [product, setProduct] = useState(null);
  const [related, setRelated] = useState([]);
  const [activeImg, setActiveImg] = useState(0);
  const [qty, setQty] = useState(1);
  const [settings, setSettings] = useState(null);
  const [productSeo, setProductSeo] = useState(null);
  const { addToCart, toggleWishlist, isWishlisted } = useCart();
  const { user } = useAuth();
  const brandName = useBrandName();
  const navigate = useNavigate();

  useEffect(() => {
    setActiveImg(0);
    setQty(1);
    window.scrollTo(0, 0);
    api.get(`/products/${slug}`).then((r) => {
      setProduct(r.data);
      if (r.data.category) {
        api.get("/products", { params: { category: r.data.category.slug, limit: 8 } })
          .then((rr) => setRelated(rr.data.items.filter((p) => p.id !== r.data.id)));
      }
    }).catch(() => setProduct(null));
    api.get("/settings").then((r) => setSettings(r.data)).catch(() => {});
    fetchProductSEO(slug).then(setProductSeo).catch(() => {});
  }, [slug]);

  if (product === null) {
    return <div className="max-w-7xl mx-auto p-12 text-center text-[#5C5C5C]">Memuat...</div>;
  }

  const price = product.sale_price || product.price;
  const hasDiscount = !!product.sale_price && product.sale_price < product.price;
  const discountPct = hasDiscount ? Math.round(((product.price - product.sale_price) / product.price) * 100) : 0;
  const wished = isWishlisted(product.id);

  const handleBuyNow = async () => {
    const ok = await addToCart(product.id, qty);
    if (ok) navigate("/cart");
  };

  const waMsg = encodeURIComponent(`Halo ${brandName || "Admin"}, saya tertarik dengan produk: ${product.name} (${formatRp(price)}). Mohon info.`);
  const waUrl = settings?.whatsapp_number ? `https://wa.me/${settings.whatsapp_number}?text=${waMsg}` : null;

  // Structured data + SEO override for this product
  const productLd = productSeo ? productJsonLd(productSeo.product, productSeo._branding) : null;
  const breadcrumbLd = breadcrumbJsonLd([
    { name: "Beranda", url: "/" },
    { name: "Katalog", url: "/katalog" },
    ...(product.category ? [{ name: product.category.name, url: `/katalog?category=${product.category.slug}` }] : []),
    { name: product.name, url: `/produk/${product.slug || slug}` },
  ]);
  const seoJsonLd = [productLd, breadcrumbLd].filter(Boolean);

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 py-6">
      {productSeo ? (
        <SEOHead seo={productSeo} jsonLd={seoJsonLd} />
      ) : (
        <SEOHead title={product.name} description={(product.description || "").slice(0, 160)} image={product.images?.[0]} jsonLd={seoJsonLd} />
      )}
      <nav className="text-xs text-[#5C5C5C] mb-4 flex items-center gap-1 flex-wrap" data-testid="breadcrumb">
        <Link to="/" className="hover:brand-text-primary">Beranda</Link>
        <ChevronRight size={12} />
        <Link to="/katalog" className="hover:brand-text-primary">Katalog</Link>
        {product.category && <>
          <ChevronRight size={12} />
          <Link to={`/katalog?category=${product.category.slug}`} className="hover:brand-text-primary">{product.category.name}</Link>
        </>}
      </nav>

      <div className="grid md:grid-cols-2 gap-6 lg:gap-10">
        {/* Gallery */}
        <div>
          <div className="aspect-square bg-white rounded-3xl border border-[#E5E5E5] overflow-hidden relative" data-testid="product-main-image">
            {product.images?.[activeImg] ? (
              <img src={imageUrl(product.images[activeImg])} alt={product.name} className="w-full h-full object-cover" />
            ) : (
              <div className="w-full h-full grid place-items-center text-[#E5E5E5]">No image</div>
            )}
            {hasDiscount && (
              <span className="absolute top-4 left-4 brand-bg-primary text-white text-xs font-bold px-3 py-1.5 rounded-lg">
                -{discountPct}% OFF
              </span>
            )}
          </div>
          {product.images?.length > 1 && (
            <div className="mt-3 flex gap-2 overflow-x-auto no-scrollbar">
              {product.images.map((img, i) => (
                <button
                  key={`${img}-${i}`}
                  onClick={() => setActiveImg(i)}
                  className={`shrink-0 w-20 h-20 rounded-xl overflow-hidden border-2 ${
                    activeImg === i ? "brand-border-primary" : "border-[#E5E5E5]"
                  }`}
                >
                  <img src={imageUrl(img)} alt="" className="w-full h-full object-cover" />
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Info */}
        <div>
          <div className="flex items-center gap-2 mb-2">
            {product.is_bestseller && (
              <span className="brand-bg-primary text-white text-[10px] font-bold px-2 py-1 rounded-lg uppercase">Terlaris</span>
            )}
            {product.is_featured && (
              <span className="bg-[#FFB703]/15 text-[#B5790F] text-[10px] font-bold px-2 py-1 rounded-lg uppercase">Unggulan</span>
            )}
          </div>
          <h1 className="font-display text-2xl sm:text-3xl lg:text-4xl font-black tracking-tight leading-tight" data-testid="product-title">
            {product.name}
          </h1>
          <div className="mt-2 flex items-center gap-3 text-sm text-[#5C5C5C]">
            {product.rating_avg > 0 && (
              <span className="flex items-center gap-1">
                <Star size={14} fill="#FFB703" stroke="#FFB703" />
                <span className="font-bold text-[#1A1A1A]">{product.rating_avg.toFixed(1)}</span>
                <span>({product.rating_count} ulasan)</span>
              </span>
            )}
            <span>•</span>
            <span>{product.sold_count || 0} terjual</span>
            <span>•</span>
            <span>SKU: {product.sku || "-"}</span>
          </div>

          <div className="mt-5 p-5 brand-soft-bg rounded-2xl">
            <div className="flex items-baseline gap-3">
              <div className="brand-text-primary font-display font-black text-3xl sm:text-4xl" data-testid="product-price">
                {formatRp(price)}
              </div>
              {hasDiscount && (
                <div className="text-[#5C5C5C] line-through text-base">{formatRp(product.price)}</div>
              )}
              <div className="ml-auto text-xs uppercase font-bold text-[#5C5C5C]">per {product.unit}</div>
            </div>
          </div>

          <div className="mt-5 grid grid-cols-2 gap-3 text-sm">
            <div className="bg-white border border-[#E5E5E5] rounded-xl p-3">
              <div className="text-[10px] uppercase tracking-widest text-[#5C5C5C] font-bold">Stok</div>
              <div className="font-bold text-base">{product.stock} {product.unit}</div>
            </div>
            <div className="bg-white border border-[#E5E5E5] rounded-xl p-3">
              <div className="text-[10px] uppercase tracking-widest text-[#5C5C5C] font-bold">Berat</div>
              <div className="font-bold text-base">{(product.weight_grams / 1000).toFixed(1)} kg</div>
            </div>
          </div>

          <div className="mt-5">
            <div className="text-xs uppercase tracking-widest brand-text-primary font-bold mb-2">Jumlah</div>
            <div className="flex items-center gap-3">
              <div className="flex items-center bg-white border border-[#E5E5E5] rounded-xl">
                <button onClick={() => setQty(Math.max(1, qty - 1))} className="w-10 h-10 grid place-items-center hover:bg-[#FAF9F6]" data-testid="qty-minus">
                  <Minus size={14} />
                </button>
                <div className="w-12 text-center font-bold" data-testid="qty-display">{qty}</div>
                <button onClick={() => setQty(Math.min(product.stock, qty + 1))} className="w-10 h-10 grid place-items-center hover:bg-[#FAF9F6]" data-testid="qty-plus">
                  <Plus size={14} />
                </button>
              </div>
              <div className="text-sm text-[#5C5C5C]">Subtotal: <span className="font-bold text-[#1A1A1A]">{formatRp(price * qty)}</span></div>
            </div>
          </div>

          <div className="mt-6 flex flex-col sm:flex-row gap-3">
            <Button
              onClick={() => addToCart(product.id, qty)}
              disabled={product.stock <= 0}
              variant="outline"
              className="flex-1 h-12 rounded-xl brand-border-primary brand-text-primary hover:brand-soft-bg hover:brand-text-primary font-bold"
              data-testid="add-to-cart-btn"
            >
              <ShoppingCart size={16} className="mr-1" /> + Keranjang
            </Button>
            <Button
              onClick={handleBuyNow}
              disabled={product.stock <= 0}
              className="flex-1 h-12 rounded-xl brand-btn-primary font-bold"
              data-testid="buy-now-btn"
            >
              Beli Sekarang
            </Button>
            <Button
              variant="outline"
              size="icon"
              onClick={() => toggleWishlist(product.id)}
              className={`h-12 w-12 rounded-xl ${wished ? "brand-bg-primary brand-border-primary text-white hover:opacity-90 hover:text-white" : ""}`}
              data-testid="wishlist-btn"
            >
              <Heart size={16} fill={wished ? "white" : "none"} />
            </Button>
          </div>

          {waUrl && (
            <a href={waUrl} target="_blank" rel="noreferrer" data-testid="wa-order-btn"
              className="mt-3 flex items-center justify-center gap-2 h-12 rounded-xl bg-[#25D366] hover:bg-[#1ea952] text-white font-bold transition-colors">
              <MessageCircle size={16} /> Order via WhatsApp
            </a>
          )}

          <div className="mt-6 grid grid-cols-2 gap-3 text-xs">
            <div className="flex items-center gap-2 text-[#5C5C5C]">
              <Truck size={16} className="brand-text-primary" /> Pengiriman cepat seluruh Indonesia
            </div>
            <div className="flex items-center gap-2 text-[#5C5C5C]">
              <ShieldCheck size={16} className="brand-text-primary" /> Garansi telur fresh
            </div>
          </div>
        </div>
      </div>

      {/* Description */}
      <section className="mt-12">
        <h2 className="font-display text-2xl font-bold mb-3">Deskripsi Produk</h2>
        <div className="bg-white border border-[#E5E5E5] rounded-2xl p-5 sm:p-6 text-[#1A1A1A] leading-relaxed whitespace-pre-wrap">
          {product.description || "Belum ada deskripsi."}
        </div>
      </section>

      {/* Reviews */}
      <section className="mt-12">
        <h2 className="font-display text-2xl font-bold mb-3">Ulasan Pelanggan</h2>
        {product.reviews?.length === 0 ? (
          <div className="bg-white border border-[#E5E5E5] rounded-2xl p-8 text-center text-[#5C5C5C]">
            Belum ada ulasan untuk produk ini.
          </div>
        ) : (
          <div className="space-y-3">
            {product.reviews?.map((r) => (
              <div key={r.id} className="bg-white border border-[#E5E5E5] rounded-2xl p-5">
                <div className="flex items-center gap-2 mb-1">
                  <div className="w-8 h-8 rounded-full brand-soft-bg brand-text-primary grid place-items-center font-bold text-xs">
                    {(r.user_name || "?")[0]}
                  </div>
                  <div>
                    <div className="font-semibold text-sm">{r.user_name || "Anonim"}</div>
                    <div className="flex items-center gap-0.5">
                      {Array.from({ length: 5 }).map((_, i) => (
                        <Star key={`${r.id}-star-${i}`} size={12} fill={i < r.rating ? "#FFB703" : "transparent"} stroke="#FFB703" />
                      ))}
                    </div>
                  </div>
                </div>
                <p className="text-sm text-[#5C5C5C] mt-2">{r.comment}</p>
              </div>
            ))}
          </div>
        )}
      </section>

      {/* Related */}
      {related.length > 0 && (
        <section className="mt-12">
          <h2 className="font-display text-2xl font-bold mb-3">Produk Terkait</h2>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3 sm:gap-4">
            {related.slice(0, 4).map((p) => <ProductCard key={p.id} product={p} />)}
          </div>
        </section>
      )}
    </div>
  );
}
